'use client'

import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence, motion } from 'framer-motion'
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch'
import { IconDownload, IconX } from '@tabler/icons-react'

import { getMessageImageStoragePath } from '@/lib/media/safe-url'
import { cn } from '@/lib/utils'
import { Button } from './button'

interface ImageModalProps {
  isOpen: boolean
  onClose: () => void
  src: string
  alt?: string
}

export function ImageModal({ isOpen, onClose, src, alt = '' }: ImageModalProps) {
  const [isDownloading, setIsDownloading] = useState(false)
  const [imageLoaded, setImageLoaded] = useState(false)
  const [imageError, setImageError] = useState(false)
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  useEffect(() => {
    if (!isOpen) return

    setImageLoaded(false)
    setImageError(false)

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [isOpen, src])

  const handleDownload = async () => {
    if (!src) return
    setIsDownloading(true)
    try {
      const storagePath = getMessageImageStoragePath(src)
      const downloadUrl = storagePath
        ? `/api/image/download?path=${encodeURIComponent(storagePath)}`
        : `/api/image/download?url=${encodeURIComponent(src)}`
      const response = /^data:|^blob:/i.test(src)
        ? await fetch(src)
        : await fetch(downloadUrl)
      if (!response.ok) throw new Error('Network response was not ok')
      const blob = await response.blob()
      const blobUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = `Losi_image_${Date.now()}.png`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(blobUrl)
    } catch (error) {
      console.error('Download failed:', error)
    } finally {
      setIsDownloading(false)
    }
  }

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  if (!mounted) {
    return null
  }

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[2147483647] flex items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={handleBackdropClick}
        >
          {/* Background Blur */}
          <motion.div
            className="absolute inset-0 bg-black/70 backdrop-blur-lg"
            initial={{ backdropFilter: 'blur(0px)' }}
            animate={{ backdropFilter: 'blur(12px)' }}
            exit={{ backdropFilter: 'blur(0px)' }}
            transition={{ duration: 0.3 }}
          />

          {/* Close Button */}
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-4 top-4 z-10 size-10 rounded-full bg-black/30 text-white hover:bg-black/50"
            onClick={onClose}
            aria-label="Close image preview"
          >
            <IconX size={20} />
          </Button>

          {/* Download Button */}
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-16 top-4 z-10 size-10 rounded-full bg-black/30 text-white hover:bg-black/50"
            onClick={handleDownload}
            disabled={isDownloading}
            aria-label="Download image"
          >
            <IconDownload size={20} />
          </Button>

          {/* Image Container - Full Screen */}
          <motion.div
            className="absolute inset-0"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
          >
            <TransformWrapper
              initialScale={1}
              minScale={0.3}
              maxScale={8}
              wheel={{ step: 0.15 }}
              pinch={{ step: 5 }}
              doubleClick={{ mode: 'reset' }}
              centerOnInit
              limitToBounds={false}
            >
              <TransformComponent
                wrapperClass="!w-screen !h-screen overflow-visible"
                contentClass="!w-screen !h-screen flex items-center justify-center overflow-visible"
              >
                {imageError ? (
                  <div className="rounded-lg bg-black/40 px-4 py-3 text-sm text-white">
                    Image unavailable
                  </div>
                ) : (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    src={src}
                    alt={alt}
                    className={cn(
                      'max-h-[calc(100dvh-2rem)] max-w-[calc(100vw-2rem)] rounded-lg object-contain transition-opacity duration-300',
                      imageLoaded ? 'opacity-100' : 'opacity-0'
                    )}
                    onLoad={() => setImageLoaded(true)}
                    onError={() => {
                      setImageLoaded(true)
                      setImageError(true)
                    }}
                    style={{
                      width: 'auto',
                      height: 'auto'
                    }}
                  />
                )}
              </TransformComponent>
            </TransformWrapper>

            {/* Loading state */}
            {!imageLoaded && !imageError && (
              <motion.div
                className="absolute inset-0 flex items-center justify-center"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <div className="size-8 animate-spin rounded-full border-2 border-white border-t-transparent" />
              </motion.div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  )
}
